import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { certificateAPI, verificationAPI } from '../api/documents';
import '../styles/Verification.css';

function Verify() {
  const [searchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentType, setDocumentType] = useState(null); // 'certificate', 'letter', or 'new-cert'

  const detectDocumentType = (value) => {
    if (!value) return null;
    if (value.startsWith('RBL-')) return 'new-cert';
    if (value.includes('/LTR/')) return 'letter';
    return 'certificate';
  };

  const handleVerify = useCallback(async (documentNumber = inputValue) => {
    if (!documentNumber.trim()) {
      setError('Please enter a document number');
      return;
    }

    const type = detectDocumentType(documentNumber);
    setDocumentType(type);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let data;
      if (type === 'new-cert') {
        data = await certificateAPI.verify(documentNumber);
        // Normalize results for UI
        setResult({
          success: data.verified,
          ...data
        });
      } else if (type === 'letter') {
        data = await verificationAPI.verifyLetter(documentNumber);
        setResult(data);
      } else {
        data = await verificationAPI.verifyCertificate(documentNumber);
        setResult(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inputValue]);

  useEffect(() => {
    const certFromUrl = searchParams.get('cert');
    const refFromUrl = searchParams.get('ref');
    const docNumber = certFromUrl || refFromUrl;

    if (docNumber) {
      setInputValue(docNumber);
      handleVerify(docNumber);
    }
  }, [searchParams, handleVerify]);

  const downloadPDF = () => {
    const target = result.data || result;
    if (target && target.id) {
      if (result.type === 'certificate' || documentType === 'new-cert') {
        certificateAPI.downloadPDF(target.id);
      }
    }
  };

  return (
    <div className="verification-page">
      <div className="verification-container">
        <div className="verification-header">
          <h1>🔍 Document Verification</h1>
          <p>Verify the authenticity of Robolog Automation credentials</p>
        </div>

        <div className="verification-search">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter document number (e.g., RBL-XXXX or RBL/INT/...)"
            className="verification-input"
            onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
          />
          <button
            onClick={() => handleVerify()}
            className="btn-verify"
            disabled={loading}
          >
            {loading ? '⏳ Verifying...' : '✓ Verify'}
          </button>
        </div>

        {error && (
          <div className="verification-result verification-error">
            <div className="result-icon">❌</div>
            <h2>Verification Failed</h2>
            <p>{error}</p>
          </div>
        )}

        {result && !result.success && !result.verified && (
          <div className="verification-result verification-invalid">
            <div className="result-icon">⚠️</div>
            <h2>Invalid Document</h2>
            <p>The document number you entered could not be found in our records.</p>
          </div>
        )}

        {/* New Certificate Engine Result */}
        {result && (result.verified || (result.success && documentType === 'new-cert')) && (
          <div className="verification-result verification-valid">
            <div className="result-icon">✅</div>
            <h2>✔ Verified by Robolog Automation</h2>

            <div className="certificate-details">
              {result.type === 'letter' ? (
                <>
                  <div className="detail-row">
                    <span className="detail-label">To:</span>
                    <span className="detail-value">{result.data.to_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Subject:</span>
                    <span className="detail-value">{result.data.subject}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Issue Date:</span>
                    <span className="detail-value">
                      {new Date(result.data.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </>
              ) : result.data ? (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{result.data.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Domain:</span>
                    <span className="detail-value">{result.data.domain}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">College:</span>
                    <span className="detail-value">{result.data.college}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">
                      {new Date(result.data.start_date).toLocaleDateString()} - {new Date(result.data.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Student:</span>
                    <span className="detail-value">{result.student_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Course:</span>
                    <span className="detail-value">{result.course}</span>
                  </div>
                </>
              )}
            </div>

            <button onClick={downloadPDF} className="btn-download-cert">
              📥 View Digital Copy
            </button>

            <div className="verification-footer">
              <p>This credential is officially issued and secured by Robolog Automation.</p>
            </div>
          </div>
        )}

        {/* Old Certificate/Letter results omitted for brevity in logic but kept for backward compatibility */}
        {/* ... (UI logic handled above covers most cases) */}

        {!result && !error && !loading && (
          <div className="verification-info">
            <h3>How to verify?</h3>
            <ol>
              <li>Enter the document number from your certificate</li>
              <li>Scan the QR code printed on the document</li>
              <li>Instant verification from Robolog's database</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

export default Verify;
